import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useTranslation } from 'react-i18next';
import { useAggregation } from 'src/hooks/useAggregation';

import Link from '@/components/link';
import { useConfigIds } from '@/ratings/useConfigIds';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import {
  controlDetailsUrl,
  obligationDetailsUrl,
  oversightAssessmentDetailsUrl,
  policyDetailsUrl,
  riskDetailsUrl,
} from '@/utils/urls';

import type {
  OversightAssessmentResultFields,
  OversightAssessmentResultRegisterFields,
} from './types';
import { useLabelledFields } from './useLabelledFields';

const getParentLink = (item: OversightAssessmentResultRegisterFields) => {
  const originalItem = item?.originalResult;
  switch (originalItem.ResultType) {
    case 'document_oversight_result':
      if (originalItem?.documents?.[0]?.document?.Id) {
        return policyDetailsUrl(originalItem?.documents?.[0]?.document?.Id);
      }
      break;
    case 'obligation_oversight_result':
      if (originalItem?.obligations?.[0]?.obligation?.Id) {
        return obligationDetailsUrl(
          originalItem?.obligations?.[0]?.obligation?.Id
        );
      }
      break;
    case 'risk_uncontrolled_oversight_result':
    case 'risk_controlled_oversight_result':
      if (originalItem?.risks?.[0]?.risk?.Id) {
        return riskDetailsUrl(originalItem?.risks?.[0]?.risk?.Id);
      }
      break;
    case 'control_test_oversight_result':
      if (originalItem?.ParentControlId) {
        return controlDetailsUrl(originalItem.ParentControlId);
      }
      break;
  }

  return null;
};

const useGetFieldConfig = (
  handleOpenRating: (id: string) => void,
  configIds: string[]
): TableFields<OversightAssessmentResultRegisterFields> => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.columns',
  });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: `complianceMonitoringAssessment.fields`,
  });
  const { t: tt } = useTranslation(['common'], {
    keyPrefix: 'columns',
  });
  const { getByValue: getByResultValue } = useRating(
    'performance_result',
    'oversight'
  );
  const { getByValue: getEffectivenessByValue } = useRating(
    'effectiveness',
    'oversight'
  );
  const { getByValue: statusGetByValue } = useRating(
    'assessment_status',
    'oversight'
  );
  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('oversight', {
      configType: 'oversight',
      configIds,
    });
  const { riskModel } = useAggregation();

  const ratingFns = {
    document_oversight_result: (d: OversightAssessmentResultFields) =>
      getByResultValue(d.Rating),
    obligation_oversight_result: (d: OversightAssessmentResultFields) =>
      getByResultValue(d.Rating),
    control_test_oversight_result: (d: OversightAssessmentResultFields) =>
      getEffectivenessByValue(d.OverallEffectiveness),
    risk_controlled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveRiskRating({
        likelihood: d.Likelihood,
        impact: d.Impact,
        controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
        rating: d.Rating,
        configId: 'ConfigId' in d ? d.ConfigId : undefined,
      }),
    risk_uncontrolled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveRiskRating({
        likelihood: d.Likelihood,
        impact: d.Impact,
        controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
        rating: d.Rating,
        configId: 'ConfigId' in d ? d.ConfigId : undefined,
      }),
  };

  const likelihoodFns = {
    document_oversight_result: (_d: OversightAssessmentResultFields) => null,
    obligation_oversight_result: (_d: OversightAssessmentResultFields) => null,
    control_test_oversight_result: (_d: OversightAssessmentResultFields) =>
      null,
    risk_uncontrolled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveLikelihood(d.Likelihood, 'ConfigId' in d ? d.ConfigId : undefined),
    risk_controlled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveLikelihood(d.Likelihood, 'ConfigId' in d ? d.ConfigId : undefined),
  };

  const impactFns = {
    document_oversight_result: (_d: OversightAssessmentResultFields) => null,
    obligation_oversight_result: (_d: OversightAssessmentResultFields) => null,
    control_test_oversight_result: (_d: OversightAssessmentResultFields) =>
      null,
    risk_uncontrolled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveImpact(d.Impact, 'ConfigId' in d ? d.ConfigId : undefined),
    risk_controlled_oversight_result: (d: OversightAssessmentResultFields) =>
      resolveImpact(d.Impact, 'ConfigId' in d ? d.ConfigId : undefined),
  };

  return {
    TestDate: dateColumnFromConfig({
      header: {
        header: t('TestDate'),
      },
      dateField: 'TestDate',
      onClick: (item: OversightAssessmentResultRegisterFields) =>
        handleOpenRating(item.Id as string),
    }),
    AssessmentTitle: {
      header: t('Title'),
      isRowHeader: true,
      cell: (item: OversightAssessmentResultRegisterFields) => {
        const oversightAssessment =
          item.originalResult.oversightAssessments.find(
            (a) => a.oversightAssessment
          )?.oversightAssessment;

        return oversightAssessment ? (
          <Link href={oversightAssessmentDetailsUrl(oversightAssessment.Id)}>
            {oversightAssessment.Title}
          </Link>
        ) : (
          '-'
        );
      },
    },
    TypeLabelled: {
      header: t('Type'),
    },
    ParentTitle: {
      header: t('Item'),
      cell: (item: OversightAssessmentResultRegisterFields) => {
        const link = getParentLink(item);

        return link ? (
          <Link href={link}>{item.ParentTitle}</Link>
        ) : (
          <>{item.ParentTitle}</>
        );
      },
    },
    RatingLabelled: {
      header: t('Result'),
      cell: (item: OversightAssessmentResultRegisterFields) => {
        const isRiskAssessmentResult =
          item.originalResult.ResultType ===
            'risk_controlled_oversight_result' ||
          item.originalResult.ResultType ===
            'risk_uncontrolled_oversight_result';
        if (
          isRiskAssessmentResult &&
          riskModel === 'control_effectiveness_averages'
        ) {
          return '-';
        }

        return (
          <SimpleRatingBadge
            rating={ratingFns[item.originalResult.ResultType](
              item.originalResult
            )}
          />
        );
      },
      sortingField: 'Rating',
    },
    ImpactLabelled: {
      header: t('Impact'),
      cell: (item: OversightAssessmentResultRegisterFields) => {
        const isRiskAssessmentResult =
          item.originalResult.ResultType ===
            'risk_controlled_oversight_result' ||
          item.originalResult.ResultType ===
            'risk_uncontrolled_oversight_result';
        if (isRiskAssessmentResult) {
          const impact = impactFns[item.originalResult.ResultType](
            item.originalResult
          );

          if (impact) {
            return <SimpleRatingBadge rating={impact} />;
          }
        }

        return <>{'-'}</>;
      },
    },
    LikelihoodLabelled: {
      header: t('Likelihood'),
      cell: (item: OversightAssessmentResultRegisterFields) => {
        const isRiskAssessmentResult =
          item.originalResult.ResultType ===
            'risk_controlled_oversight_result' ||
          item.originalResult.ResultType ===
            'risk_uncontrolled_oversight_result';
        if (isRiskAssessmentResult) {
          const likelihood = likelihoodFns[item.originalResult.ResultType](
            item.originalResult
          );

          if (likelihood) {
            return <SimpleRatingBadge rating={likelihood} />;
          }
        }

        return '-';
      },
    },
    StartDate: dateColumnFromConfig({
      header: {
        header: t('StartDate'),
      },
      dateField: 'StartDate',
    }),
    ActualCompletionDate: dateColumnFromConfig({
      header: {
        header: t('CompletionDate'),
      },
      dateField: 'ActualCompletionDate',
    }),
    CompletedByUser: {
      header: t('CompletionBy'),
    },
    Rationale: {
      header: t('Rationale'),
    },
    StatusLabelled: {
      header: st('Status'),
      cell: (item: OversightAssessmentResultRegisterFields) => {
        return <SimpleRatingBadge rating={statusGetByValue(item.Status)} />;
      },
    },
    Id: {
      header: tt('guid'),
    },
  };
};

export const useGetCollectionTableProps = (
  records: OversightAssessmentResultFields[] | undefined,
  handleOpenRating: (id: string) => void
): TablePropsWithActions<OversightAssessmentResultRegisterFields> => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'oversightAssessmentResults',
  });

  const configIds = useConfigIds(records);

  const fields = useGetFieldConfig(handleOpenRating, configIds);
  const labelledFields = useLabelledFields(records, configIds);

  return useGetTableProps({
    customAttributeFormIds: [],
    tableId: 'oversightAssessmentResultRegister',
    data: labelledFields,
    entityLabel: at('entity_name'),
    emptyCollectionAction: <></>,
    preferencesStorageKey: 'OversightAssessmentResultRegister-Preferences',
    enableFiltering: true,
    initialColumns: [
      'TestDate',
      'AssessmentTitle',
      'ParentTitle',
      'TypeLabelled',
      'ImpactLabelled',
      'LikelihoodLabelled',
      'RatingLabelled',
      'StatusLabelled',
    ],
    fields,
  });
};
